import { Button } from 'reactstrap';
import classnames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import React, { useState } from 'react';
import { ShiftType } from '@ms/timesheets/dist/models/shift-type';
import { useDispatch, useSelector } from 'react-redux';
import { clearChangedTimeEntries } from 'features/pending-timesheets/redux/time-entries-slice';
import {
    clearSelectedTimesheets,
    setSelectedTimesheet,
} from 'features/pending-timesheets/redux/selected-timesheet-slice';
import { IOnlineApprovalTimeEntry } from 'features/pending-timesheets/models/online-approval-time-entry.model';
import { PENDING_TIMESHEETS } from 'api/use-query-constants';
import { PendingTimesheet } from 'features/pending-timesheets/models/pending-timesheets.model';
import { RootState } from 'store/root-reducer';
import SmartComponent from 'utils/smart-component';
import renderMobile from './timesheet-row.mobile';
import TimesheetEditModal from './modals/timesheet-edit-modal.component';
import { TypedLocalState } from 'utils/redux-hooks';
import { UpdateLogic } from 'features/pending-timesheets/logic/update.logic';
import { useEditTimeEntriesMutation } from 'features/pending-timesheets/hooks/edit-time-entries-mutation.hook';
import 'features/pending-timesheets/styles/timesheet-row.scss';
import { toggleSelection } from '../redux/pending-timesheets-slice';
import { setDisablingSpinnerVisible } from 'redux/global-elements-slice';
import { getQueryClient } from '../../config/config';

export interface ITimesheetRowProps {
    payPeriodStart: Dayjs;
    payPeriodEnd: Dayjs;
    timesheet: PendingTimesheet;
    isLast?: boolean;
}

export interface ITimesheetRowState extends ITimesheetRowProps {
    highlightedShiftTypes: ShiftType[];
    payPeriodStart: Dayjs;
    payPeriodEnd: Dayjs;
    showEditModal: boolean;
    showMobileModal: boolean;
    setShowMobileModal: (show: boolean) => void;
    save: (newTimesheet: PendingTimesheet, supervisorComment: string) => void;
    toggleRowSelection: (timesheet: PendingTimesheet) => void;
    viewEdit: (viewEditTimesheet: PendingTimesheet) => void;
    viewEditMobile: (viewEditTimesheet: PendingTimesheet) => void;
    close: () => void;
}

const useInitialState = (props: ITimesheetRowProps): ITimesheetRowState => {
    const [showMobileModal, setShowMobileModal]: TypedLocalState<boolean> = useState(false);
    const [showEditModal, setShowEditModal]: TypedLocalState<boolean> = useState(false);

    const dispatch = useDispatch();

    const url = useSelector((state: RootState) => state.pendingTimesheets.url as string);

    const { mutate: SaveTimeEntriesMutation } = useEditTimeEntriesMutation(url, () => {
        setShowEditModal(false);
    });

    const selectTimesheet = (newTimesheet: PendingTimesheet) => {
        dispatch(clearSelectedTimesheets());
        dispatch(clearChangedTimeEntries());
        dispatch(setSelectedTimesheet(newTimesheet));
    };

    const toggleRowSelection = (timesheet: PendingTimesheet) => dispatch(toggleSelection({ timesheet }));

    const viewEdit = (viewEditTimesheet: PendingTimesheet) => {
        selectTimesheet(viewEditTimesheet);
        setShowEditModal(true);
    };

    const viewEditMobile = (viewEditTimesheet: PendingTimesheet) => {
        selectTimesheet(viewEditTimesheet);
        setShowMobileModal(true);
    };

    const updatedPendingTimesheets = useSelector(
        (state: RootState) => state.pendingTimesheets.changedTimesheets as PendingTimesheet[],
    );
    const timesheets = useSelector((state: RootState) => state.pendingTimesheets.timesheets as PendingTimesheet[]);

    const changedTimeEntries = useSelector(
        (state: RootState) => state.timeEntries.changedTimeEntries as IOnlineApprovalTimeEntry[],
    );

    const highlightedShiftTypes = changedTimeEntries
        .filter((entry: IOnlineApprovalTimeEntry) => entry.timesheetId === props.timesheet.timesheetId)
        .map((entry: IOnlineApprovalTimeEntry) => entry.shiftType);

    const save = (newTimesheet: PendingTimesheet, supervisorComment: string) => {
        const newTimesheetWithComment = { ...newTimesheet, supervisorComment: supervisorComment };
        const updatedTimesheets = UpdateLogic.updateTimesheet(newTimesheetWithComment, updatedPendingTimesheets);

        SaveTimeEntriesMutation({
            newEntries: changedTimeEntries,
            timesheetId: newTimesheet?.timesheetId,
            supervisorComments: supervisorComment,
            updatedTimesheets,
            timesheets,
        });
        dispatch(setDisablingSpinnerVisible(true));
    };

    const close = () => {
        setShowEditModal(false);
        getQueryClient().setQueryData(PENDING_TIMESHEETS, updatedPendingTimesheets);
        dispatch(clearChangedTimeEntries());
    };

    return {
        ...props,
        highlightedShiftTypes,
        save,
        showEditModal,
        showMobileModal,
        setShowMobileModal,
        toggleRowSelection,
        viewEdit,
        viewEditMobile,
        close,
    };
};

const renderDesktop = ({
    highlightedShiftTypes,
    showEditModal,
    save,
    timesheet,
    toggleRowSelection,
    viewEdit,
    close,
}: ITimesheetRowState): JSX.Element => (
    <tr className="timesheet-row">
        <td className="checkbox">
            {!timesheet.onlineApprovalDate ? (
                <input
                    id={`select-${timesheet.timesheetId}`}
                    type="checkbox"
                    checked={timesheet.isSelected}
                    onChange={() => toggleRowSelection(timesheet)}
                    disabled={!!timesheet.onlineApprovalDate}
                />
            ) : null}
        </td>

        <td>
            <label htmlFor={`select-${timesheet.timesheetId}`} className="m-0 cursor-pointer">
                <span className="sr-only">Select </span>
                {timesheet.travelerFirstName} {timesheet.travelerLastName}
            </label>
        </td>
        <td>{timesheet.unitName}</td>
        <td
            className={classnames({
                'font-weight-bold': highlightedShiftTypes.indexOf(ShiftType.Regular) >= 0,
            })}
        >
            {timesheet.regularHours?.toFixed(2)}
        </td>
        <td
            className={classnames({
                'font-weight-bold': highlightedShiftTypes.indexOf(ShiftType.OnCall) >= 0,
            })}
        >
            {timesheet.onCallHours?.toFixed(2)}
        </td>
        <td
            className={classnames({
                'font-weight-bold': highlightedShiftTypes.indexOf(ShiftType.Callback) >= 0,
            })}
        >
            {timesheet.callbackHours?.toFixed(2)}
        </td>
        <td
            className={classnames({
                'font-weight-bold': highlightedShiftTypes.indexOf(ShiftType.Charge) >= 0,
            })}
        >
            {timesheet.chargeHours?.toFixed(2)}
        </td>
        <td
            className={classnames({
                'font-weight-bold': highlightedShiftTypes.length > 0,
            })}
        >
            {timesheet.totalHours?.toFixed(2)}
        </td>
        <td className="text-center">
            <Button color="link" onClick={() => viewEdit(timesheet)}>
                {!timesheet?.onlineApprovalDate ? 'View / Edit' : 'View'}
                <span className="sr-only">
                    {' '}
                    {timesheet.travelerFirstName} {timesheet.travelerLastName}'s timesheet (Opens a popup)
                </span>
            </Button>
        </td>
        {showEditModal && (
            <TimesheetEditModal
                payPeriodStart={dayjs(timesheet.payPeriodStart)}
                payPeriodEnd={dayjs(timesheet.payPeriodEnd)}
                isOpen={showEditModal}
                close={close}
                save={save}
            />
        )}
    </tr>
);

export { renderDesktop as TimesheetRowDesktop, renderMobile as TimesheetRowMobile };

export default SmartComponent<ITimesheetRowProps, ITimesheetRowState>(useInitialState, renderDesktop, renderMobile);
